<template>
    <div
        class="video-editor weflix-admin-newVideo"
        data-aos="fade-left"
        data-aos-duration="1500"
    >
        <main class="video-editor__container" v-if="hasReady">
            <nav class="video-editor__breadcrumb" aria-label="Trilha">
                <router-link to="/admin/colecoes">Coleções</router-link>
                <span class="video-editor__breadcrumb-sep" aria-hidden="true"><fa icon="fa-chevron-right" /></span>
                <router-link :to="'/admin/colecao/' + currentCollections.id">{{ collectionName }}</router-link>
                <span class="video-editor__breadcrumb-sep" aria-hidden="true"><fa icon="fa-chevron-right" /></span>
                <span>Novo vídeo</span>
            </nav>

            <header class="video-editor__header">
                <div>
                    <h1 class="video-editor__title">Novo Vídeo</h1>
                    <p class="video-editor__subtitle">Crie um novo vídeo para sua coleção.</p>
                </div>
                <aside class="video-editor__notice">
                    <span class="video-editor__notice-icon" aria-hidden="true"><fa icon="fa-video" /></span>
                    <div>
                        <strong>Este vídeo será exibido na sua coleção</strong>
                        <p>Configure as informações e envie o arquivo.</p>
                    </div>
                </aside>
            </header>

            <div class="video-editor__grid">
                <div class="video-editor__column">
                    <section class="video-editor__card video-editor__card--info">
                        <header class="video-editor__card-header">
                            <span class="video-editor__card-icon" aria-hidden="true"><fa icon="fa-file-lines" /></span>
                            <div>
                                <h2 class="video-editor__card-title">Informações do vídeo</h2>
                                <p class="video-editor__card-description">Defina os dados principais da aula.</p>
                            </div>
                        </header>

                        <div class="video-editor__field-grid">
                            <div class="video-editor__field">
                                <label class="video-editor__label" for="new-video-status">Status</label>
                                <div class="video-editor__select-wrap" :class="status ? 'is-on' : 'is-off'">
                                    <span class="video-editor__status-dot" aria-hidden="true"></span>
                                    <select id="new-video-status" class="video-editor__select" v-model="status">
                                        <option :value="true">Ativado</option>
                                        <option :value="false">Desativado</option>
                                    </select>
                                </div>
                            </div>
                            <div class="video-editor__field">
                                <label class="video-editor__label" for="new-video-visibility">Visibilidade</label>
                                <div class="video-editor__select-wrap" :class="{ 'is-locked': sample === false }">
                                    <fa v-if="sample === false" icon="fa-lock" class="video-editor__lock" />
                                    <select id="new-video-visibility" class="video-editor__select" v-model="sample">
                                        <option :value="true">Gratuito</option>
                                        <option :value="false">Pago</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div class="video-editor__field video-editor__field-block">
                            <label class="video-editor__label" for="new-video-title">Título do vídeo</label>
                            <input
                                id="new-video-title"
                                type="text"
                                required
                                class="video-editor__input"
                                v-model="name"
                                placeholder="Ex.: Fundamentos da Gestão de Projetos"
                            />
                        </div>

                        <div class="video-editor__field video-editor__field-block">
                            <label class="video-editor__label" for="new-video-description">Descrição do vídeo</label>
                            <textarea
                                id="new-video-description"
                                class="video-editor__textarea"
                                v-model="longDescription"
                                maxlength="300"
                                placeholder="Descreva o que o aluno vai aprender nesta aula..."
                            ></textarea>
                            <small class="video-editor__counter">{{ descriptionCount }}/300 caracteres</small>
                        </div>
                    </section>

                    <section class="video-editor__card video-editor__card--tips">
                        <header class="video-editor__card-header">
                            <span class="video-editor__card-icon" aria-hidden="true"><fa icon="fa-lightbulb" /></span>
                            <div>
                                <h2 class="video-editor__card-title">Dicas e boas práticas</h2>
                                <p class="video-editor__card-description">Siga estas recomendações para uma melhor experiência dos alunos.</p>
                            </div>
                        </header>
                        <div class="video-editor__tips">
                            <div class="video-editor__tip">
                                <span class="video-editor__tip-icon"><fa icon="fa-video" /></span>
                                <strong>Formato</strong>
                                <p>MP4 recomendado ou MOV.</p>
                            </div>
                            <div class="video-editor__tip">
                                <span class="video-editor__tip-icon"><fa icon="fa-clock" /></span>
                                <strong>Duração</strong>
                                <p>Preferencialmente entre 5 e 60 minutos.</p>
                            </div>
                            <div class="video-editor__tip">
                                <span class="video-editor__tip-icon"><fa icon="fa-tv" /></span>
                                <strong>Resolução</strong>
                                <p>Full HD — 1920 × 1080.</p>
                            </div>
                            <div class="video-editor__tip">
                                <span class="video-editor__tip-icon"><fa icon="fa-star" /></span>
                                <strong>Qualidade</strong>
                                <p>Áudio nítido e boa iluminação.</p>
                            </div>
                        </div>
                    </section>
                </div>

                <div class="video-editor__column">
                    <section class="video-editor__card video-editor__card--file">
                        <header class="video-editor__card-header">
                            <span class="video-editor__card-icon" aria-hidden="true"><fa icon="fa-cloud-arrow-up" /></span>
                            <div>
                                <h2 class="video-editor__card-title">Arquivo do vídeo</h2>
                                <p class="video-editor__card-description">Envie o arquivo e salve para o player ficar disponível.</p>
                            </div>
                        </header>
                        <VideoUploadInput
                            label=""
                            :preview-url="path"
                            :file-name="selectedVideoName"
                            :file-size="selectedVideoSize"
                            accept=".mp4, .mov, .webm, .avi"
                            accept-text="Formatos aceitos conforme as regras do sistema."
                            placeholder="Clique para enviar o vídeo"
                            :uploading="isUploading"
                            :progress="progress"
                            @file-selected="handleVideoSelected"
                            @remove-preview="handleVideoRemove"
                        />
                        <video
                            ref="hiddenVideo"
                            style="display: none;"
                            @loadedmetadata="onVideoLoaded"
                            @seeked="onVideoSeeked"
                        ></video>
                        <canvas ref="thumbnailCanvas" style="display: none;"></canvas>
                    </section>

                    <section class="video-editor__card video-editor__card--cover">
                        <header class="video-editor__card-header">
                            <span class="video-editor__card-icon" aria-hidden="true"><fa icon="fa-image" /></span>
                            <div>
                                <h2 class="video-editor__card-title">Capa do vídeo</h2>
                                <p class="video-editor__card-description">A capa será gerada automaticamente do vídeo.</p>
                            </div>
                        </header>
                        <div class="video-editor__cover">
                            <img v-if="previewCoverVideo" :src="previewCoverVideo" alt="Capa do vídeo" />
                            <div v-else class="video-editor__cover-empty">
                                <fa icon="fa-image" />
                                <span>A capa será gerada automaticamente</span>
                            </div>
                        </div>
                        <div class="video-editor__info">
                            <fa icon="fa-circle-info" />
                            <span>A capa será gerada automaticamente do vídeo. Você poderá substituí-la posteriormente editando o vídeo.</span>
                        </div>
                    </section>
                </div>
            </div>
        </main>

        <footer class="video-editor__footer">
            <div class="video-editor__footer-inner">
                <button
                    type="button"
                    class="video-editor__btn video-editor__btn--back"
                    @click="$router.push('/admin/colecao/' + currentCollections.id)"
                >
                    <fa icon="fa-arrow-left" />
                    Voltar
                </button>
                <button
                    type="button"
                    class="video-editor__btn video-editor__btn--save"
                    :disabled="!loaderImage || (Boolean(path) && !coverVideo)"
                    @click="newVideo()"
                >
                    <fa icon="fa-floppy-disk" />
                    Salvar vídeo
                </button>
            </div>
        </footer>
    </div>

    <div class="pa-4 text-center" style="padding: 0px!important;">
        <v-dialog v-model="deleteItem" max-width="320" persistent>
            <v-card>
                <v-card-title class="headline">Atenção</v-card-title>
                <v-card-text>Deseja realmente excluir o vídeo {{ nameItem }}?</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn class="cancel" text @click="closeDialog">Não</v-btn>
                    <v-btn class="accept" text @click="closeDialog">Sim</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
    <v-dialog v-model="showCopyDialog" class="copy-video-dialog" max-width="600">
        <v-card>
            <v-card-title>Copiar Vídeo</v-card-title>
            <v-card-text>
                <div class="move-controls">
                    <p>Selecione a coleção de origem</p>
                    <select v-model="selectedCollection" class="form-select" @change="putIdReuseVideoCollection()">
                        <option value="">Selecione uma coleção</option>
                        <option v-for="collection in collections" :key="collection.id" :value="collection.id">
                            {{ collection.name }}
                        </option>
                    </select>
                </div>
            </v-card-text>
            <v-card-text>
                <div class="move-controls">
                    <p>Selecione o vídeo</p>
                    <select v-model="selectedLesson" class="form-select" @change="getVideoById()">
                        <option value="">Selecione um vídeo</option>
                        <option v-for="videos in lessonByCollection" :key="videos.id" :value="videos.id">
                            {{ videos.name }}
                        </option>
                    </select>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="showCopyDialog = false">Cancelar</v-btn>
                <v-btn color="primary" text @click="copyVideo">Copiar</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
    <Loader :hasReady="hasReady"/>
</template>

<script>
import Sesson from '@/utils/Weflix/sesson';
import Videos from '@/utils/Weflix/videos';
import apiAWS from '@/utils/storage/aws';
import companyApi from '@/utils/Weflix/company';
import Loader from '@/components/Weflix/Loader.vue';
import LoaderImage from '@/components/Weflix/LoaderImage';
import VideoUploadInput from '@/components/Weflix/VideoUploadInput.vue';
import TitlePage from '@/components/Weflix/titlePage.vue';
import { mapState } from 'vuex';
import { toast } from 'vue3-toastify';

function emptyVideotecaUpload() {
    return {
        fileId: null,
        callbackUrl: null,
        uploadId: null,
        partUrls: [],
        partSize: 0,
        partCount: 0,
        fileUploadUrl: null,
        thumbnailUploadUrl: null,
        file: null,
        filename: null,
        mediaType: null,
        size: 0,
        parts: [],
        thumbnailUploaded: false,
    };
}

export default {
    data(){
        return{
            deleteItem: false,
            season:'',
            seasonSelected: null,
            noSeason: true,
            status: false,
            sample: false,
            name: '',
            longDescription: '',
            videoFile: null,
            coverVideo: '',
            previewCoverVideo:'',
            linkVideo: '',
            hasReady: true,
            loaderImage: true,
            tokenVideo: null,
            isUploading: false,
            isUploaded: false,
            progress: 0,
            showCopyDialog: false,
            selectedCollection: '',
            selectedLesson: '',
            collections: [],
            lessonByCollection: [],
            idReuseVideoCollection: null,
            path: null,
            activeUploads: [],
            uploadIdCounter: 0,
            videoObjectUrl: null,
            videotecaUpload: emptyVideotecaUpload(),
            videotecaFolderId: null,
            pendingCoverFile: null,
            canvasCoverFile: null,
        }
    },
    computed: {
        ...mapState(['token', 'currentCollections']),
        collectionName() {
            const current = this.currentCollections;
            if (!current || Array.isArray(current)) return 'Coleção';
            return current.name || current.comercialName || 'Coleção';
        },
        descriptionCount() {
            return (this.longDescription || '').length;
        },
        selectedVideoName() {
            return this.videoFile && this.videoFile.name ? this.videoFile.name : '';
        },
        selectedVideoSize() {
            return this.videoFile && Number(this.videoFile.size) ? Number(this.videoFile.size) : 0;
        },
    },
    components: {
        Loader,
        LoaderImage,
        VideoUploadInput,
        TitlePage
    },
    methods: {
        closeDialog(){
            this.deleteItem = false;
        },
        updateContentLongDescription(content) {
            this.longDescription = content;
        },
        async getVideoById(){
            const response = await Videos.getVideosById(this.token, this.selectedLesson);
            this.name = response.title || '';
            this.longDescription = response.description || '';
            this.coverVideo = response.videoThumbnail || '';
            this.previewCoverVideo = response.finalVideoThumbnail || '';
            this.sample = response.sample == 1 ? true : false;
            this.status = response.status == 1 ? true : false;
            this.path = response.path || '';
           
        },
        async copyVideo(){
            this.showCopyDialog = false;
        },
        async getSesson(){
            try {
                const response = await Sesson.getSessonByCollection(this.token, this.currentCollections.id);
                if(response.length > 0) {
                    if (Array.isArray(response)) {
                        this.season = response.sort((a, b) => a.order - b.order);
                        this.seasonSelected = this.season[0].id;
                        this.noSeason = false;
                    } else {
                        this.season = response;
                        this.seasonSelected = this.season[0].id;
                        this.noSeason = false;
                    }
                }
                else {
                    this.noSeason = true;
                    this.newSeason();
                }
            } catch (error) {
                console.error('Erro ao obter dados da coleção:', error);
            }
        },
        async newSeason(){
            const payload = {
                "course": {
                    "id": this.currentCollections.id
                },
                "title": "Temporada 1",
                "sample": 0
            }
            try {
                const response = await Sesson.createSesson(this.token, payload);
                this.noSeason = false;
                this.getSesson();
            } catch (error) {
                console.error('Erro ao criar temporada:', error);
            }
        },     
        async putFileAws(url, file){
            await apiAWS.putFileAWS(url, file);
        },
        handleVideoSelected(file, event) {
            this.uploadVideoAws(event);
            // Capturar frame do vídeo em 0.5s
            this.captureVideoThumbnail(file);
        },
        handleVideoRemove() {
            if (this.path) {
                URL.revokeObjectURL(this.path);
                this.path = null;
                this.videoFile = null;
            }
            this.progress = 0;
            this.isUploading = false;
            // Limpar também a capa quando o vídeo for removido
            this.handleImageRemove();
        },
        handleImageRemove() {
            if (this.previewCoverVideo) {
                URL.revokeObjectURL(this.previewCoverVideo);
                this.previewCoverVideo = '';
                this.coverVideo = '';
            }
        },
        captureVideoThumbnail(file) {
            if (!file) return;
            
            this.loaderImage = false;
            const video = this.$refs.hiddenVideo;
            const canvas = this.$refs.thumbnailCanvas;
            
            if (!video || !canvas) {
                console.error('Elementos de vídeo ou canvas não encontrados');
                this.loaderImage = true;
                return;
            }
            
            // Limpar URL anterior se existir
            if (this.videoObjectUrl) {
                URL.revokeObjectURL(this.videoObjectUrl);
                this.videoObjectUrl = null;
            }
            
            // Criar URL do vídeo
            const videoUrl = URL.createObjectURL(file);
            video.src = videoUrl;
            this.videoObjectUrl = videoUrl;
            
            // Adicionar tratamento de erro
            video.onerror = () => {
                console.error('Erro ao carregar vídeo para captura de thumbnail');
                this.loaderImage = true;
                if (this.videoObjectUrl) {
                    URL.revokeObjectURL(this.videoObjectUrl);
                    this.videoObjectUrl = null;
                }
            };
        },
        onVideoLoaded() {
            const video = this.$refs.hiddenVideo;
            if (!video) return;
            
            // Verificar se o vídeo tem duração válida
            if (video.duration && video.duration > 0) {
                // Pular para 0.5 segundos (ou início se o vídeo for menor)
                video.currentTime = Math.min(0.5, video.duration - 0.1);
            } else {
                // Se não conseguir obter a duração, tentar mesmo assim
                video.currentTime = 0.5;
            }
        },
        async onVideoSeeked() {
            const video = this.$refs.hiddenVideo;
            const canvas = this.$refs.thumbnailCanvas;
            
            if (!video || !canvas) {
                console.error('Elementos de vídeo ou canvas não encontrados');
                this.loaderImage = true;
                return;
            }
            
            // Verificar se o vídeo tem dimensões válidas
            if (!video.videoWidth || !video.videoHeight) {
                console.error('Vídeo não possui dimensões válidas');
                this.loaderImage = true;
                return;
            }
            
            try {
                // Configurar dimensões do canvas
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                
                // Desenhar frame do vídeo no canvas
                const ctx = canvas.getContext('2d');
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                
                // Converter canvas para blob
                canvas.toBlob(async (blob) => {
                    if (!blob) {
                        console.error('Erro ao gerar thumbnail do vídeo');
                        this.loaderImage = true;
                        return;
                    }
                    
                    // Limpar preview anterior se existir
                    if (this.previewCoverVideo) {
                        URL.revokeObjectURL(this.previewCoverVideo);
                    }
                    
                    // Criar preview
                    this.previewCoverVideo = URL.createObjectURL(blob);
                    
                    // Criar arquivo a partir do blob para upload
                    const file = new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' });
                    this.canvasCoverFile = file;
                    
                    // Fazer upload da imagem
                    try {
                        await this.uploadCoverFile(file);
                        this.loaderImage = true;
                    } catch (error) {
                        console.error('Erro ao enviar capa para a Videoteca', error);
                        this.coverVideo = '';
                        this.loaderImage = false;
                        this.notify('Não foi possível enviar a capa gerada do vídeo.', 'error');
                    }
                }, 'image/jpeg', 0.95);
                
            } catch (error) {
                console.error('Erro ao capturar thumbnail do vídeo:', error);
                this.loaderImage = true;
            }
        },
        async ensureVideotecaFolderId() {
            if (this.videotecaFolderId) return this.videotecaFolderId;
            const status = await companyApi.getVideoteca(this.token);
            const folderId = status?.folderId || status?.idFolder || status?.idFolderVideoteca || null;
            if (!folderId) throw new Error('VIDEOTECA_FOLDER_MISSING');
            this.videotecaFolderId = String(folderId);
            return this.videotecaFolderId;
        },
        async uploadCoverFile(file) {
            const response = await apiAWS.getSignatureImage(this.token, file.name || 'thumbnail.jpg');
            const token = response?.token;
            if (!response?.url || !token) {
                throw new Error('Assinatura da capa sem token');
            }
            await this.putFileAws(response.url, file);
            this.coverVideo = token;

            const thumbUrl = this.videotecaUpload?.thumbnailUploadUrl;
            if (!thumbUrl) {
                this.pendingCoverFile = file;
                return token;
            }
            await this.putFileAws(thumbUrl, file);
            this.videotecaUpload.thumbnailUploaded = true;
            this.pendingCoverFile = null;
            return token;
        },
        async flushPendingCover() {
            const file = this.pendingCoverFile;
            const thumbUrl = this.videotecaUpload?.thumbnailUploadUrl;
            if (!file || !thumbUrl) return;
            await this.putFileAws(thumbUrl, file);
            this.videotecaUpload.thumbnailUploaded = true;
            this.pendingCoverFile = null;
        },
        async uploadVideoAws(event) {
            const localUploadId = ++this.uploadIdCounter;
            try {
                const file = event.target.files[0];
                if (!file) return;

                if (Math.round(file.size / (1024 * 1024)) > 4096) {
                    this.notify('O arquivo passa de 4 GB.', 'error');
                    return;
                }

                this.activeUploads.push({
                    id: localUploadId,
                    filename: file.name,
                    fileSize: file.size,
                    progress: 0,
                    speed: 0,
                    isUploading: false,
                    isCompleted: false,
                    error: null,
                    startTime: null,
                    lastProgressTime: null,
                    lastProgressBytes: 0,
                });

                this.path = URL.createObjectURL(file);
                this.videoFile = file;
                this.videotecaUpload = emptyVideotecaUpload();
                this.isUploaded = false;
                this.progress = 0;

                const folderId = await this.ensureVideotecaFolderId();
                const title = (this.name || '').trim() || file.name.replace(/\.[^.]+$/, '') || file.name;
                const initResponse = await apiAWS.initVideotecaUpload(this.token, {
                    filename: file.name,
                    mediaType: file.type || 'video/mp4',
                    folderId,
                    title,
                    size: file.size,
                    metadata: {},
                });
                if (!initResponse) throw new Error('URLs de upload inválidas');

                const data = initResponse.data || initResponse;
                const multipart = data.multipart || null;
                const partUrls = Array.isArray(multipart?.partUrls)
                    ? [...multipart.partUrls].sort((a, b) => Number(a.partNumber) - Number(b.partNumber))
                    : [];
                const uploadId = multipart?.uploadId || data.uploadId || initResponse.uploadId || null;
                if (partUrls.length > 0 && !uploadId) {
                    throw new Error('Init multipart sem uploadId — não é possível finalizar o upload.');
                }

                this.videotecaUpload = {
                    ...emptyVideotecaUpload(),
                    fileId: data.fileId || null,
                    callbackUrl: data.callbackUrl || null,
                    uploadId,
                    partUrls,
                    partSize: Number(multipart?.partSize) || 0,
                    partCount: Number(multipart?.partCount) || partUrls.length || 0,
                    fileUploadUrl: data.fileUploadUrl || null,
                    thumbnailUploadUrl: data.thumbnailUploadUrl || null,
                    file,
                    filename: file.name,
                    mediaType: file.type || 'video/mp4',
                    size: file.size,
                };
                this.tokenVideo = {
                    uploadId,
                    urls: partUrls.map((part) => ({ url: part.url, token: null, partNumber: part.partNumber })),
                };
                await this.flushPendingCover();
                await this.putVideoAwsV1(this.videotecaUpload, localUploadId);
            } catch (error) {
                console.error('Erro ao enviar arquivo para a Videoteca', error);
                const uploadIndex = this.activeUploads.findIndex((u) => u.id === localUploadId);
                if (uploadIndex !== -1) {
                    this.activeUploads[uploadIndex].error = error?.message === 'VIDEOTECA_FOLDER_MISSING'
                        ? 'A empresa não tem pasta na Videoteca.'
                        : (error.message || 'Erro no upload');
                    this.activeUploads[uploadIndex].isUploading = false;
                }
                if (error?.message === 'VIDEOTECA_FOLDER_MISSING') {
                    this.notify('A empresa não tem pasta na Videoteca.', 'error');
                }
                this.videotecaUpload = emptyVideotecaUpload();
                this.isUploading = false;
                this.isUploaded = false;
            }
        },
        async putVideoAwsV1(uploadState, localUploadId) {
            const uploadIndex = this.activeUploads.findIndex((u) => u.id === localUploadId);
            const uploadEntry = uploadIndex === -1 ? null : this.activeUploads[uploadIndex];
            const file = uploadState.file;
            if (!file) throw new Error('URLs de upload inválidas');

            if (uploadEntry) {
                uploadEntry.isUploading = true;
                uploadEntry.startTime = Date.now();
                uploadEntry.lastProgressTime = Date.now();
                uploadEntry.lastProgressBytes = 0;
            }
            this.isUploading = true;
            this.isUploaded = false;

            const partUrls = uploadState.partUrls || [];
            const parts = [];

            if (partUrls.length > 0) {
                const partSize = Number(uploadState.partSize) || Math.ceil(file.size / partUrls.length);
                const progressArray = new Array(partUrls.length).fill(0);
                for (let i = 0; i < partUrls.length; i += 1) {
                    const part = partUrls[i];
                    const partNumber = Number(part.partNumber) || (i + 1);
                    const start = i * partSize;
                    const end = Math.min(start + partSize, file.size);
                    const chunk = file.slice(start, end);
                    const response = await apiAWS.putVideoAWS(part.url, chunk, (progress) => {
                        progressArray[i] = progress;
                        const totalProgress = progressArray.reduce((a, b) => a + b, 0) / partUrls.length;
                        this.progress = totalProgress;
                        if (uploadEntry) uploadEntry.progress = totalProgress;
                    });
                    if (!response?.etag) throw new Error(`ETag ausente na parte ${partNumber}`);
                    parts.push({ partNumber, etag: response.etag });
                }
            } else if (uploadState.fileUploadUrl) {
                await apiAWS.putVideoAWS(uploadState.fileUploadUrl, file, (progress) => {
                    this.progress = progress;
                    if (uploadEntry) uploadEntry.progress = progress;
                });
            } else {
                throw new Error('URLs de upload inválidas');
            }

            this.videotecaUpload = {
                ...this.videotecaUpload,
                parts: uploadState.uploadId ? parts : [],
            };
            this.isUploaded = true;
            this.isUploading = false;
            this.progress = 100;
            if (uploadEntry) {
                uploadEntry.isCompleted = true;
                uploadEntry.isUploading = false;
                uploadEntry.progress = 100;
                uploadEntry.speed = 0;
                setTimeout(() => {
                    const index = this.activeUploads.findIndex((u) => u.id === localUploadId);
                    if (index !== -1) this.activeUploads.splice(index, 1);
                }, 3000);
            }
        },
        handleCancelUpload(uploadId) {
            const uploadIndex = this.activeUploads.findIndex(u => u.id === uploadId);
            if (uploadIndex !== -1) {
                this.activeUploads[uploadIndex].isUploading = false;
                this.activeUploads[uploadIndex].error = 'Upload cancelado pelo usuário';
                
                // Limpar preview se for o upload atual
                if (this.activeUploads[uploadIndex].id === this.uploadIdCounter) {
                    if (this.path) {
                        URL.revokeObjectURL(this.path);
                        this.path = null;
                        this.videoFile = null;
                    }
                }
                
                // Remover após um tempo
                setTimeout(() => {
                    const index = this.activeUploads.findIndex(u => u.id === uploadId);
                    if (index !== -1) {
                        this.activeUploads.splice(index, 1);
                    }
                }, 2000);
            }
        },
        async getCollections(){
            try {
                const response = await Videos.listCollections(this.token);
                this.collections = response.map(item => ({
                    id: item.id,
                    name: item.name,
                    status: item.status
                }));
            } catch (error) {
                console.error('Erro ao obter coleções:', error);
            }
        },
        putIdReuseVideoCollection(){
            this.idReuseVideoCollection = this.selectedCollection;
            this.getLessonByCollections();
        },
        async getLessonByCollections(){
            try {
                const response = await Videos.listLessonByCollections(this.token, this.idReuseVideoCollection);
                this.lessonByCollection = response;
            } catch (error) {
                console.error('Erro ao obter aulas:', error);
            }
        },
        /** Corpo do POST /file/videoteca/upload: lesson direta ou dentro de data/body/result. */
        extractCreatedLessonId(created) {
            if (created == null) return null;
            if (typeof created === 'string') {
                const t = created.trim();
                if (!t) return null;
                if (t.startsWith('{') || t.startsWith('[')) {
                    try {
                        return this.extractCreatedLessonId(JSON.parse(t));
                    } catch {
                        return null;
                    }
                }
                return t;
            }
            if (typeof created !== 'object') return null;
            if (created.id != null && created.id !== '') {
                return String(created.id);
            }
            const nests = [
                created.data,
                created.body,
                created.result,
                created.payload,
                created.lesson,
                created.content,
            ];
            for (const n of nests) {
                if (n != null && typeof n === 'object' && !Array.isArray(n)) {
                    if (n.id != null && n.id !== '') return String(n.id);
                }
            }
            return null;
        },
        miniLessonsAsArray(mini) {
            if (Array.isArray(mini)) return mini;
            if (mini?.list != null) return mini.list;
            if (mini?.content != null) return mini.content;
            return [];
        },
        async resolveLessonIdAfterVideotecaCreate(createdPayload) {
            let id = this.extractCreatedLessonId(createdPayload);
            if (id) return id;
            const title = (this.name || '').trim();
            const pickByTitle = (list, titleKey = 'title') => {
                if (!list?.length) return null;
                const same = list.filter(
                    (v) => (v[titleKey] || v.name || '').trim() === title
                );
                const row =
                    same.length > 0 ? same[same.length - 1] : list[list.length - 1];
                return row?.id != null ? String(row.id) : null;
            };
            if (this.seasonSelected) {
                try {
                    const mod = await Videos.getVideosBySesson(
                        this.token,
                        this.seasonSelected
                    );
                    id = pickByTitle(mod?.list || [], 'title');
                    if (id) return id;
                } catch (e) {
                    console.error('Erro ao listar vídeos do módulo:', e);
                }
            }
            if (this.currentCollections?.id) {
                try {
                    const mini = await Videos.listLessonByCollections(
                        this.token,
                        this.currentCollections.id
                    );
                    const list = this.miniLessonsAsArray(mini);
                    id = pickByTitle(list, 'name');
                    if (id) return id;
                } catch (e) {
                    console.error('Erro ao listar aulas da coleção (mini):', e);
                }
            }
            return null;
        },
        async loadVideotecaStatus() {
            try {
                const status = await companyApi.getVideoteca(this.token);
                this.videotecaFolderId = status?.folderId || status?.idFolder || status?.idFolderVideoteca || null;
            } catch (error) {
                console.warn('Não foi possível carregar status da Videoteca:', error);
            }
        },
        async newVideo(){
            this.hasReady = false;

            let videoThumbnail = this.coverVideo || null;
            try {
                if (this.canvasCoverFile) {
                    videoThumbnail = await this.uploadCoverFile(this.canvasCoverFile);
                }
            } catch (error) {
                console.error('Erro ao enviar a capa do vídeo', error);
                this.notify('Não foi possível enviar a capa gerada do vídeo.', 'error');
                this.hasReady = true;
                return;
            }
            if ((this.path || this.canvasCoverFile) && !videoThumbnail) {
                this.notify('A capa gerada do vídeo não foi enviada.', 'error');
                this.hasReady = true;
                return;
            }

            const upload = this.videotecaUpload || {};
            const parts = Array.isArray(upload.parts) && upload.parts.length ? upload.parts : null;
            const payload = {
                type: 6,
                title: this.name,
                description: this.longDescription,
                idModule: this.seasonSelected,
                status: this.status === true ? 1 : 0,
                sample: this.sample === true ? 1 : 0,
                favourite: null,
                fileId: upload.fileId || null,
                callbackUrl: upload.callbackUrl || null,
                filename: upload.filename || null,
                mediaType: upload.mediaType || null,
                size: upload.size || null,
                thumbnailUploaded: Boolean(upload.thumbnailUploaded),
                path: null,
                youtubeUrl: null,
                videoThumbnail,
                token: null,
            };
            if (upload.uploadId && parts) {
                payload.uploadId = upload.uploadId;
                payload.parts = parts;
            }

            try {
                const created = await apiAWS.createVideoVideoteca(
                    this.token,
                    payload
                );
                let lessonId = this.extractCreatedLessonId(created);
                if (!lessonId) {
                    lessonId = await this.resolveLessonIdAfterVideotecaCreate(
                        created
                    );
                }
                if (lessonId != null && lessonId !== '') {
                    await this.$router.replace({
                        path: `/admin/video/${encodeURIComponent(lessonId)}`,
                    });
                } else {
                    this.notify(
                        'Vídeo criado, mas não foi possível abrir a edição. Abra-o na lista da coleção.',
                        'warning'
                    );
                    await this.$router.replace(
                        '/admin/colecao/' + this.currentCollections.id
                    );
                }
            } catch (error) {
                console.error('Erro ao enviar arquivo para AWS', error);
                this.notify(
                    'Erro ao criar o vídeo. Tente novamente.',
                    'error'
                );
            } finally {
                this.hasReady = true;
            }
        },
        notify(message, type = "info") {
            toast(message, {
                autoClose: 3000,
                position: 'top-right',
                theme: "light", 
                limit: 1,
                type: type,
                dangerouslyHTMLString: true
            });
        },
    },  
    created(){
        this.getSesson();
        this.getCollections();
        this.loadVideotecaStatus();
    },
    beforeUnmount() {
        // Limpar URLs criadas
        if (this.videoObjectUrl) {
            URL.revokeObjectURL(this.videoObjectUrl);
        }
        if (this.path) {
            URL.revokeObjectURL(this.path);
        }
        if (this.previewCoverVideo) {
            URL.revokeObjectURL(this.previewCoverVideo);
        }
    }
}
</script>

<style>
    @import url('@/css/weflix/video-editor.css');
</style>
